import * as amplitude from "@amplitude/analytics-browser";
import UserIdentifier from "./userIdentifier";
import PricingService from "./pricingService";
import { trackPurchase, trackSignUp } from "./ga4";
import { captureForTest } from "./trackingTestCapture";
import type { GA4SignUpMethod } from "./ga4";
import type {
  AmplitudeTrackingEventProperties,
  AmplitudeTrackingEventType,
  PurchaseType,
} from "@/types/amplitude-tracking.type";
import type { TiktokEvent, TiktokParams } from "@/types/tiktok-tracking.type";
import type { Cents } from "@/types/pricing.type";
import type { FlowType } from "@/lib/funnelFlow";
import { getUTMParams } from "@/lib/utmTracking";
import { AmplitudeTrackingEvent } from "@/types/amplitude-tracking.type";

const logger = (message: string, values?: unknown) => {
  const isDev = import.meta.env.DEV;

  if (isDev) console.warn(`[Tracking]: ${message}`, values ?? {});
};

const compactMatching = (
  params: Record<string, string>,
): Record<string, string> =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => value));

export type PaymentMethod = "apple_pay" | "google_pay" | "card";
export type { FlowType };

export type SubscriptionTrigger =
  | "initial_paywall"
  | "locked_episode"
  | "delayed_paywall"
  | "series_details"
  | "upsell";

// Meta `Subscribe` counts the subscription alone (see Tracking in CLAUDE.md).
// A `Record` so a new trigger fails `tsc` until someone decides.
const SENDS_META_SUBSCRIBE: Record<SubscriptionTrigger, boolean> = {
  initial_paywall: true,
  locked_episode: true,
  delayed_paywall: true,
  series_details: true,
  upsell: false,
};

class Tracking {
  private isInitialized = false;
  private flowType: FlowType = "default";

  /**
   * The analytics identity, set from wherever the user is stored rather than
   * at init only: `init` runs after `load`, `Register` answers when it does,
   * and whichever comes second must not leave the session start and the
   * experiment exposures on a device-only user (SHO-852). The SDK queues the
   * call until its init.
   */
  static setUserId = (userId: string) => {
    amplitude.setUserId(userId);
  };

  setFlowType = (flowType: FlowType) => {
    this.flowType = flowType;
    const identifyEvent = new amplitude.Identify();
    identifyEvent.set("flow_type", flowType);
    amplitude.identify(identifyEvent);
  };

  /**
   * The locale the app actually renders in. Amplitude's autocapture already
   * reports a `language`, but that one reads `navigator.language` — since the
   * selector shipped (SHO-485) the two diverge the moment a user picks a
   * language, so segmenting on the browser's would answer the wrong question.
   */
  setAppLocale = (locale: string) => {
    const identifyEvent = new amplitude.Identify();
    identifyEvent.set("app_locale", locale);
    amplitude.identify(identifyEvent);
  };

  /**
   * Initialize all tracking services. Each tracker boots in isolation so
   * one failing SDK (e.g. ttq.load throwing) can't cascade and abort the
   * others — that previously left isInitialized=false and dropped every
   * subsequent track call.
   */
  init = () => {
    if (this.isInitialized) return;
    if (import.meta.env.DEV) {
      logger("No tracking in development");
      return;
    }

    const safe = (name: string, fn: () => void) => {
      try {
        fn();
      } catch (error) {
        console.error(`Error initializing ${name}:`, error);
      }
    };

    safe("amplitude", this.initAmplitude);
    safe("pricingCohort", this.initPricingCohort);
    safe("tiktokPixel", this.initTikTokPixel);
    safe("metaPixel", this.initMetaPixel);
    safe("snapchatPixel", this.initSnapchatPixel);
    safe("googleTag", this.initGoogleTag);
    safe("appsFlyer", this.initAppsFlyer);
    safe("openAiPixel", this.initOpenAiPixel);

    this.isInitialized = true;
    logger("Tracking services initialized");
  };

  initPricingCohort = () => {
    try {
      const pricingSet = PricingService.getUserPricingSet();
      logger("Pricing cohort initialized:", pricingSet.id);
    } catch (error) {
      console.error("Error initializing pricing cohort:", error);
    }
  };

  initAmplitude = () => {
    amplitude.init(import.meta.env.VITE_AMPLITUDE_API_KEY, {
      defaultTracking: true,
      autocapture: true,
    });

    amplitude.setUserId(UserIdentifier.getUserUUID());

    const utmParams = getUTMParams();
    if (Object.keys(utmParams).length > 0) {
      const identifyEvent = new amplitude.Identify();
      Object.entries(utmParams).forEach(([key, value]) => {
        identifyEvent.set(key, value);
      });
      amplitude.identify(identifyEvent);
    }

    logger("Amplitude initialized");
  };

  initTikTokPixel = () => {
    if (!window.ttq) {
      logger("TikTok Pixel not initialized");
      return;
    }

    const TIKTOK_PIXEL_ID = "CV0T4DBC77U0UQ9ILGSG";
    const userId = UserIdentifier.getUserUUID();
    window.ttq.load(TIKTOK_PIXEL_ID);
    if (userId && typeof window.ttq.identify === "function") {
      window.ttq.identify({ external_id: userId });
    }
    window.ttq.page();

    logger("TikTok Pixel initialized");
  };

  initMetaPixel = () => {
    if (typeof window === "undefined") return;
    if (typeof window.fbq !== "function") return;

    const META_PIXEL_ID = "947425004193924";
    const advancedMatching = compactMatching({
      external_id: UserIdentifier.getUserUUID(),
      em: UserIdentifier.getEmail(),
    });

    if (Object.keys(advancedMatching).length > 0) {
      window.fbq("init", META_PIXEL_ID, advancedMatching);
    } else {
      window.fbq("init", META_PIXEL_ID);
    }
    window.fbq("track", "PageView");

    logger("Meta Pixel initialized");
  };

  initSnapchatPixel = () => {
    if (!window.snaptr) {
      logger("Snapchat Pixel not initialized");
      return;
    }

    const SNAPCHAT_PIXEL_ID = "577e11dd-1e46-4328-b9bd-3882c966dcf6";
    const advancedMatching = compactMatching({
      uuid_c1: UserIdentifier.getUserUUID(),
      email: UserIdentifier.getEmail(),
    });

    if (Object.keys(advancedMatching).length > 0) {
      window.snaptr("init", SNAPCHAT_PIXEL_ID, advancedMatching);
    } else {
      window.snaptr("init", SNAPCHAT_PIXEL_ID);
    }
    window.snaptr("track", "PAGE_VIEW");

    logger("Snapchat Pixel initialized");
  };

  initGoogleTag = () => {
    if (typeof window === "undefined") return;

    const GTAG_ID = "AW-1022101353";

    gtag("js", new Date());
    gtag("config", GTAG_ID);

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GTAG_ID}`;
    document.head.appendChild(script);
    logger("Google Tag initialized");
  };

  initAppsFlyer = () => {
    if (typeof window === "undefined") return;
    if (typeof AF !== "function") return;
    AF("pba", "setCustomerUserId", UserIdentifier.getUserUUID());
    logger("AppsFlyer initialized");
  };

  initOpenAiPixel = () => {
    if (typeof window === "undefined") return;
    if (typeof window.oaiq !== "function") return;

    const OPENAI_PIXEL_ID = "KcriDF3BxGwnRqCyeejufU";
    window.oaiq("init", {
      pixelId: OPENAI_PIXEL_ID,
      debug: import.meta.env.DEV,
    });
    logger("OpenAI Ads pixel initialized");
  };

  trackEpisodeView = (
    episode: { id: string; number: number; free: boolean } | null,
    show: { id: string; title: string } | null,
  ) => {
    if (!this.isInitialized) this.init();
    if (!episode || !show) return;

    try {
      const eventProperties = {
        episode_id: episode.id,
        episode_number: episode.number,
        show_id: show.id,
        show_title: show.title,
        is_free: !!episode.free,
      };

      amplitude.logEvent("Episode View", eventProperties);

      this.trackTiktokEvent("ViewContent", {
        content_type: "episode",
        content_id: episode.id,
        content_name: `${show.title} - Episode ${episode.number}`,
      });

      this.trackSnapchatEvent("VIEW_CONTENT", {
        item_ids: [episode.id],
        uuid_c1: UserIdentifier.getUserUUID(),
      });

      logger("Tracked episode view:", eventProperties);
    } catch (error) {
      console.error("Error tracking episode view:", error);
    }
  };

  trackEpisodeFinished = (
    episode: { id: string; number: number; free: boolean } | null,
    show: { id: string; title: string } | null,
  ) => {
    if (!this.isInitialized) this.init();
    if (!episode || !show) return;

    try {
      const eventProperties: AmplitudeTrackingEventProperties[typeof AmplitudeTrackingEvent.finishedEpisode] =
        {
          "series name": show.title,
          "episode number": episode.number.toString(),
        };

      this.trackAmplitudeEvent(
        AmplitudeTrackingEvent.finishedEpisode,
        eventProperties,
      );

      this.trackMetaEvent("ViewContent", {
        content_type: "episode",
        content_ids: [episode.id],
        content_name: `${show.title} - Episode ${episode.number}`,
      });

      this.trackOpenAiEvent("contents_viewed", { type: "contents" });

      logger("Tracked episode finished:", eventProperties);
    } catch (error) {
      console.error("Error tracking episode finished:", error);
    }
  };

  resetAllTrackers = () => {
    amplitude.reset();
    this.initMetaPixel();
    this.initSnapchatPixel();
    this.initGoogleTag();
    this.initAppsFlyer();

    if (window.ttq)
      window.ttq.identify({ email: undefined, external_id: undefined });
  };

  trackUserLogin = (
    email: string,
    userId: string | undefined,
    method: GA4SignUpMethod = "email",
  ) => {
    if (typeof window === "undefined") return;

    if (typeof amplitude !== "undefined") {
      if (userId) amplitude.setUserId(userId);
      const identifyEvent = new amplitude.Identify();
      identifyEvent.set("email", email);
      amplitude.identify(identifyEvent);
      this.trackAmplitudeEvent(AmplitudeTrackingEvent.emailSubmitted, {
        email,
      });
    }

    this.initMetaPixel();
    this.trackMetaEvent("Lead");

    if (window.ttq) {
      window.ttq.identify({
        email,
      });
      this.trackTiktokEvent("Lead");
    }

    this.initSnapchatPixel();
    this.trackSnapchatEvent("LOGIN", {
      uuid_c1: userId || UserIdentifier.getUserUUID(),
      email,
    });

    if (typeof AF === "function")
      AF("pba", "setCustomerUserId", userId || UserIdentifier.getUserUUID());

    trackSignUp(method);

    logger("Email tracked:", email);
  };

  trackPaymentFunnelStep = (
    step: string,
    data: Record<string, unknown> = {},
  ) => {
    if (!this.isInitialized) this.init();

    try {
      const eventProperties = {
        funnel_step: step,
        ...data,
      };

      this.trackAmplitudeEvent(
        AmplitudeTrackingEvent.paymentFunnelStep,
        eventProperties,
      );

      switch (step) {
        case "payment_initiated":
          this.trackTiktokEvent("InitiateCheckout", {
            content_type: "product",
            content_id: "premium_monthly",
          });
          break;
        case "paywall_view":
          this.trackTiktokEvent("AddToCart", {
            content_type: "product",
            content_id: "premium_monthly",
            quantity: 1,
          });
          break;
      }

      switch (step) {
        case "payment_initiated":
          this.trackMetaEvent("InitiateCheckout");
          break;
        case "paywall_view":
          this.trackMetaEvent("AddToCart");
          break;
      }

      switch (step) {
        case "payment_initiated":
          this.trackSnapchatEvent(
            "START_CHECKOUT",
            compactMatching({
              uuid_c1: UserIdentifier.getUserUUID(),
              email: UserIdentifier.getEmail(),
            }),
          );
          break;
        case "paywall_view":
          this.trackSnapchatEvent(
            "ADD_CART",
            compactMatching({
              uuid_c1: UserIdentifier.getUserUUID(),
              email: UserIdentifier.getEmail(),
            }),
          );
          break;
      }

      switch (step) {
        case "paywall_view":
          this.trackAppsFlyerEvent({
            eventName: "view paywall",
            eventValue: eventProperties as Record<string, unknown>,
          });
          break;
      }

      switch (step) {
        case "paywall_view":
          this.trackOpenAiEvent("items_added", { type: "contents" });
          break;
      }

      logger(`Tracked payment funnel step: ${step}`, eventProperties);
    } catch (error) {
      console.error("Error tracking payment funnel step:", error);
    }
  };

  // `source` tells the entry points apart: the bottom banner keeps the
  // historical `web_popup`, the nav bar's Get the app sends its own.
  trackAppDownloadClick = (source = "web_popup") => {
    if (!this.isInitialized) this.init();

    try {
      const eventProperties = {
        action: "app_download_click",
        source,
      };

      this.trackAmplitudeEvent(
        AmplitudeTrackingEvent.appDownloadClick,
        eventProperties,
      );

      logger("Tracked app download click:", eventProperties);
    } catch (error) {
      console.error("Error tracking app download click:", error);
    }
  };

  trackPurchaseComplete = (
    transactionId: string,
    amountInCents: Cents = 1499 as Cents,
    currency = "USD",
    priceId = "monthly",
    userEmail: string | null = null,
    showTitle: string,
    purchaseType: PurchaseType,
    subscriptionTrigger: SubscriptionTrigger,
    paymentMethod?: PaymentMethod,
  ) => {
    if (!this.isInitialized) this.init();

    // Analytics/ad destinations expect major units. Convert exactly ONCE here,
    // at the tracking edge, so the whole fan-out below stays byte-identical.
    const amount = amountInCents / 100;

    try {
      const eventProperties = {
        transaction_id: transactionId,
        revenue: amount,
        currency: currency,
        subscription_plan: priceId,
      };

      const revenueObj = new amplitude.Revenue()
        .setProductId(priceId)
        .setPrice(amount)
        .setQuantity(1)
        .setRevenueType("subscription");

      amplitude.revenue(revenueObj);
      this.trackAmplitudeEvent(AmplitudeTrackingEvent.purchaseComplete, {
        ...eventProperties,
        show_title: showTitle,
        provider: "stripe",
        purchase_type: purchaseType,
        subscription_trigger: subscriptionTrigger,
        payment_method: paymentMethod,
      });

      this.trackTiktokEvent("CompletePayment", {
        content_type: "product",
        content_id: priceId,
        value: amount,
        currency: currency as TiktokParams["currency"],
        quantity: 1,
        event_id: transactionId,
      });

      const metaPurchase = {
        value: amount,
        currency: currency,
        content_type: "subscription",
        content_ids: [priceId],
      };
      this.trackMetaEvent("Purchase", metaPurchase, { eventID: transactionId });
      // Nothing persisted, a returning buyer counts. A $0 purchase (no plan)
      // never reaches Stripe, so it isn't a subscription.
      if (SENDS_META_SUBSCRIBE[subscriptionTrigger] && amount > 0) {
        this.trackMetaEvent("Subscribe", metaPurchase, {
          eventID: transactionId,
        });
      }

      this.trackSnapchatEvent("PURCHASE", {
        price: amount,
        currency: currency,
        transaction_id: transactionId,
        item_ids: [priceId],
        number_items: 1,
        uuid_c1: UserIdentifier.getUserUUID(),
        ...(userEmail ? { user_email: userEmail } : {}),
      });

      this.trackAppsFlyerEvent({
        eventName: "initiate purchase",
        eventRevenue: amount,
        eventRevenueCurrency: currency,
      });

      this.trackGoogleConversion(transactionId, amount, currency, userEmail);

      this.trackOpenAiEvent("subscription_created", {
        type: "plan_enrollment",
        amount: 0,
        currency: "USD",
      });

      trackPurchase({
        transaction_id: transactionId,
        value: amount,
        currency,
        items: [
          {
            item_id: priceId,
            item_name: priceId,
            price: amount,
            quantity: 1,
          },
        ],
      });

      logger("Tracked purchase complete:", eventProperties);
    } catch (error) {
      console.error("Error tracking purchase complete:", error);
    }
  };

  trackGoogleConversion = (
    transactionId: string,
    amount: number,
    currency: string,
    userEmail: string | null = null,
  ) => {
    if (typeof window.gtag !== "function") {
      console.warn("Google Tag (gtag) not available for conversion tracking");
      return;
    }

    try {
      if (!userEmail && typeof window !== "undefined")
        userEmail = UserIdentifier.getEmail();

      window.gtag("event", "conversion", {
        send_to: "AW-1022101353/yKa3CMXfgJgbEOmOsOcD",
        transaction_id: transactionId,
        value: amount,
        currency: currency,
        ...(userEmail ? { user_data: { email: userEmail } } : {}),
      });

      logger("Google Ads conversion tracked:", {
        transaction_id: transactionId,
        value: amount,
        currency: currency,
        enhanced_conversion: !!userEmail,
      });
    } catch (error) {
      console.error("Error tracking Google Ads conversion:", error);
    }
  };

  trackUpsellView = (upsellName: string) => {
    this.trackAmplitudeEvent(AmplitudeTrackingEvent.upsellView, {
      upsell_name: upsellName,
    });
  };

  trackUpsellAction = (
    step: number,
    productId: string,
    action: "skip" | "continue",
  ) => {
    this.trackAmplitudeEvent(AmplitudeTrackingEvent.upsellAction, {
      step,
      product: productId,
      action,
    });
  };

  trackAmplitudeEvent = <T extends AmplitudeTrackingEventType>(
    event: T,
    properties: AmplitudeTrackingEventProperties[T],
  ) => {
    captureForTest("amplitude", event, properties as Record<string, unknown>);

    if (!this.isInitialized) this.init();
    if (!this.isInitialized) return logger("Tracking not initialized");

    try {
      amplitude.track(event, properties);
      logger("Tracked Amplitude event:", { event, properties });
    } catch (error) {
      console.error("Error tracking Amplitude event:", error);
    }
  };

  trackMetaEvent = (
    event: string,
    properties?: Record<string, unknown>,
    options?: Record<string, unknown>,
  ) => {
    const payload = { ...properties, flow_type: this.flowType };
    captureForTest("meta", event, payload);

    if (!this.isInitialized) return logger("Tracking not initialized");

    if (typeof window.fbq !== "function") {
      logger("Meta Pixel not available for event tracking");
      return;
    }
    window.fbq("track", event, payload, options);
  };

  trackSnapchatEvent = (
    event: string,
    properties?: Record<string, unknown>,
  ) => {
    const payload = { ...properties, flow_type: this.flowType };
    captureForTest("snapchat", event, payload);

    if (!this.isInitialized) return logger("Tracking not initialized");

    if (!window.snaptr) {
      logger("Snapchat Pixel not available for event tracking");
      return;
    }
    window.snaptr("track", event, payload);
  };

  trackTiktokEvent = (event: TiktokEvent, properties?: TiktokParams) => {
    const payload = { ...properties, flow_type: this.flowType } as TiktokParams;
    captureForTest("tiktok", event, payload as Record<string, unknown>);

    if (!this.isInitialized) return logger("Tracking not initialized");

    if (!window.ttq) {
      logger("TikTok Pixel not available for event tracking");
      return;
    }
    window.ttq.track(event, payload, { debug: import.meta.env.DEV });
  };

  trackAppsFlyerEvent = (properties: {
    eventName: string;
    eventRevenue?: number;
    eventRevenueCurrency?: string;
    eventValue?: Record<string, unknown>;
  }) => {
    const payload = {
      ...properties,
      eventValue: {
        ...(properties.eventValue ?? {}),
        flow_type: this.flowType,
      },
      eventType: "EVENT",
    };
    captureForTest("appsflyer", properties.eventName, payload);

    if (!this.isInitialized) return logger("Tracking not initialized");

    if (typeof AF === "function") {
      try {
        AF("pba", "event", payload);
        logger("Tracked AppsFlyer event:", { properties });
      } catch (error) {
        console.error("Error tracking AppsFlyer event:", error);
      }
    }
  };

  trackOpenAiEvent = (event: string, params: Record<string, unknown>) => {
    captureForTest("openai", event, params);

    if (!this.isInitialized) return logger("Tracking not initialized");

    if (typeof window.oaiq !== "function") {
      logger("OpenAI Ads pixel not available for event tracking");
      return;
    }
    window.oaiq("measure", event, params);
  };
}

export default Tracking;
